import { useMemo } from 'react';
import type { Id, Invoice } from '@hero/schema';
import {
  formatDateMain,
  getDateDiffNow,
  getInvoiceRoleForViewer,
  getInvoiceStatusConfig,
  isSameDay,
} from '@hero/core';

/**
 * Single source of truth for the amount a payer still owes on an invoice.
 *
 * Pure function (no hooks) so it can be used inside Zustand stores,
 * `useMemo` callbacks, and regular components alike.
 *
 * Falls back to `amount.total` only when `amount.remaining` is not set.
 */
export function getInvoiceAmountToPay(invoice: Invoice): number {
  return invoice.amount.remaining ?? invoice.amount.total;
}

export type InvoiceInfo = ReturnType<typeof computeInvoiceInfo>;

function computeInvoiceInfo(invoice: Invoice, viewerOrgId?: Id | string) {
  const { role } = getInvoiceRoleForViewer(invoice, viewerOrgId);
  const isBuyer = role === 'buyer';
  const isSeller = role === 'seller';
  // Legacy alias for callers that still branch on `isPayer`. Buyer ⇔ payer.
  const isPayer = isBuyer;
  const { canMakePayment, canUploadInvoice: statusAllowsUpload } = getInvoiceStatusConfig(
    invoice.status,
  );

  const canUploadInvoice =
    statusAllowsUpload &&
    (!invoice.invoiceProof || invoice.invoiceProof.status === 'rejected');

  const canPay = canMakePayment;

  const dueDate = invoice.period.due;
  const isPaid = !!invoice.paidAt;

  const diff = getDateDiffNow(dueDate, 'day');
  const daysLeft = isPaid
    ? (() => {
        if (!invoice.paidAt) return 0;
        const paidAt = new Date(invoice.paidAt);
        const due = new Date(dueDate);
        const paidDay = new Date(paidAt.getFullYear(), paidAt.getMonth(), paidAt.getDate());
        const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate());
        return Math.round((paidDay.getTime() - dueDay.getTime()) / 86400000);
      })()
    : -diff.diff;
  const isDueToday = !isPaid && daysLeft === 0;
  const isOverdue = isPaid ? daysLeft > 0 : diff.dateHasPassed && !isDueToday;

  const dueDateDisplay = formatDateMain({ format: 'date_day', date: dueDate });

  const periodFromDisplay = formatDateMain({ format: 'date', date: invoice.period.from });
  const periodToDisplay = formatDateMain({ format: 'date', date: invoice.period.to });
  const isSameDayInvoice = isSameDay(invoice.period.from, invoice.period.to);

  const cashbackPercentage =
    invoice.cashback?.status === 'active' ? invoice.cashback.percentage : undefined;
  const hasCashback = canPay && isPayer && !!cashbackPercentage && cashbackPercentage > 0;

  const cashbackDaysLeft = (() => {
    if (!hasCashback || !invoice.cashback?.expiresAt) return undefined;
    const expiresAt = new Date(invoice.cashback.expiresAt);
    return Math.max(0, Math.ceil((expiresAt.getTime() - Date.now()) / 86400000));
  })();

  const isPaidOnTime = isPaid && daysLeft === 0;
  const isPaidEarly = isPaid && daysLeft < 0;

  const amountToPay = getInvoiceAmountToPay(invoice);

  const createdAtDisplay = invoice.doc?.createdAt
    ? formatDateMain({ format: 'date_time', date: invoice.doc.createdAt })
    : undefined;

  const paidAtDisplay = invoice.paidAt
    ? formatDateMain({ format: 'date_time', date: invoice.paidAt })
    : undefined;

  /**
   * Invoice ↔ FinancingApplication back-link. Single source of truth
   * for "can this invoice still be financed?" — the moment a
   * `FinancingApplication` targets this invoice, the backend
   * stamps `invoice.financing` and `canFinance` flips off forever.
   */
  const financing = invoice.financing;
  const financingStatus = financing?.status;
  /**
   * `canFinance` mirrors the backend's `applyFinanceableFilters`:
   * only `not_paid` / `partially_paid` invoices that don't yet
   * carry a `financing` back-link are eligible. Seller-only is
   * still up to the caller — the chip / CTA gates on
   * `isSeller && canFinance`.
   */
  const canFinance =
    !financing && (invoice.status === 'not_paid' || invoice.status === 'partially_paid');

  return {
    /** The amount remaining to be paid on this invoice (single source of truth). */
    amountToPay,
    canPay,
    canUploadInvoice,
    dueDate,
    daysLeft,
    isOverdue,
    isDueToday,
    dueDateDisplay,
    periodFromDisplay,
    isSameDayInvoice,
    periodToDisplay,
    /** Bilateral viewer role helpers. */
    isBuyer,
    isSeller,
    /** Legacy alias — true when the viewer is the buyer (same as `isBuyer`). */
    isPayer,
    /** The role the viewer plays on this invoice — `'buyer' | 'seller' | 'observer'`. */
    viewerRole: role,
    /** Whether the invoice has been fully paid */
    isPaid,
    /** Paid exactly on the due date */
    isPaidOnTime,
    /** Paid before the due date */
    isPaidEarly,
    /** Cashback percentage as decimal (e.g. 0.02 for 2%). undefined when no cashback. */
    cashbackPercentage: hasCashback ? cashbackPercentage : undefined,
    /** Whether cashback incentive is active */
    hasCashback,
    /** Days left for cashback urgency. undefined if overdue or no cashback. */
    cashbackDaysLeft,
    /** Formatted creation date (e.g. "Feb 12, 11:00 AM") */
    createdAtDisplay,
    /** Formatted payment date (e.g. "Feb 12, 11:00 AM"). undefined if not paid. */
    paidAtDisplay,
    /**
     * Back-link to the `FinancingApplication` (if any) targeting this
     * invoice. Presence = "this invoice already has a financing
     * application" — single source of truth for `canFinance` below.
     */
    financing,
    /**
     * Coarse 4-state lifecycle of `invoice.financing` —
     * `pending` | `financed` | `rejected` | `cancelled`, or
     * `undefined` when no application has been submitted yet.
     */
    financingStatus,
    /**
     * Whether this invoice can be financed today. Reflects the
     * backend `applyFinanceableFilters` predicate — closed invoices
     * (`paid`, `cancelled`, `pending`) and invoices already linked
     * to a `FinancingApplication` flip this to `false`. Use this
     * as the gating condition for "Finance it now" CTAs/chips.
     */
    canFinance,
  };
}

/**
 * Derives computed information from an invoice.
 *
 * When `invoice` is provided, returns a guaranteed `InvoiceInfo` object.
 * When `invoice` is `null` or `undefined`, returns `undefined`.
 *
 * Pass `viewerOrgId` to enable the bilateral `isBuyer` / `isSeller` /
 * `viewerRole` fields. When omitted, those flags default to "observer"
 * — pay/upload/finance CTAs that depend on role should treat that
 * as "do not show".
 */
export function useInvoiceInfo(invoice: Invoice, viewerOrgId?: Id | string): InvoiceInfo;
export function useInvoiceInfo(
  invoice: Invoice | null | undefined,
  viewerOrgId?: Id | string,
): InvoiceInfo | undefined;
export function useInvoiceInfo(
  invoice: Invoice | null | undefined,
  viewerOrgId?: Id | string,
): InvoiceInfo | undefined {
  return useMemo(
    () => (invoice ? computeInvoiceInfo(invoice, viewerOrgId) : undefined),
    [
      invoice?.status,
      invoice?.period?.due,
      invoice?.period?.from,
      invoice?.period?.to,
      invoice?.paidAt,
      invoice?.daysToPay,
      invoice?.cashback,
      invoice?.amount?.remaining,
      invoice?.amount?.total,
      invoice?.doc?.createdAt,
      invoice?.invoiceProof?.status,
      invoice?.buyer?._id,
      invoice?.seller?._id,
      invoice?.financing?._id,
      invoice?.financing?.status,
      viewerOrgId,
    ],
  );
}
